/** True when `fetchedAt` is missing or older than `ttlMs` — the shared TTL check backing
 *  `creditStore`'s balance/pricing-packages caches (mirrors `global-surface/service/whatsNew`'s
 *  `shouldRefetch`). Balance stays live server state (contract 11 §7) but a short TTL, combined
 *  with persisting the fetch, means a hard reload within the window reuses the cached read instead
 *  of refetching unconditionally. */
export function shouldRefetch(fetchedAt: number | null, ttlMs: number, now = Date.now()): boolean {
  return fetchedAt === null || now - fetchedAt >= ttlMs
}

/** Balance is per-account usage that changes as credits are spent — short enough that a stale
 *  read is never mistaken for current, long enough to absorb repeat mounts/reloads in one sitting. */
export const BALANCE_TTL_MS = 2 * 60 * 1000

/** The public subscription-pricing catalog changes rarely and is not account-scoped — a longer
 *  TTL than balance is safe. */
export const PRICING_PACKAGES_TTL_MS = 10 * 60 * 1000

export function isPricingCatalogFresh(loaded: boolean, fetchedAt: number | null): boolean {
  return loaded && !shouldRefetch(fetchedAt, PRICING_PACKAGES_TTL_MS)
}

/** `cachedAccountId`/`fetchedAt` are the scope a prior fetch landed for — both must match the
 *  current account before the cache is trusted, so a switch never reads another tenant's cache. */
export function isBalanceFresh(
  loaded: boolean,
  cachedAccountId: number | null,
  currentAccountId: number,
  fetchedAt: number | null,
): boolean {
  return loaded && cachedAccountId === currentAccountId && !shouldRefetch(fetchedAt, BALANCE_TTL_MS)
}
